import Checkbox from 'components/Form/Checkbox';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import Information from 'components/Information';
import Stacked from 'components/Layout/Stacked';
import LinkButton from 'components/LinkButton';
import {FC, useCallback, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';
import {onApiFormError} from 'utils/request/result';

import {PostDeletePreviewResponse, usePostDeletePreview} from '../services';
import {DeletePartner, DeletionSubmissionFormData, UserAccount} from '../types';
import useDeletionConfirmFormDefaultValues, {
    createPartnerIds,
    getPartnerObjectFromObject,
} from '../utils';

export type DeletionFormProps = {
    deletePartners: Array<DeletePartner>;
    hasMultiplePartners: boolean;
    hasReasons: boolean;
    onSuccess: (data: {
        formData: DeletionSubmissionFormData;
        previewData: PostDeletePreviewResponse;
    }) => void;
    userAccount: UserAccount;
    usingVba?: boolean;
};

const HomeDeletionForm: FC<DeletionFormProps> = ({
    deletePartners,
    hasMultiplePartners,
    hasReasons,
    onSuccess,
    userAccount,
    usingVba,
}) => {
    const defaultValues = useDeletionConfirmFormDefaultValues(deletePartners);
    const methods = useForm<FormFields<DeletionSubmissionFormData>>({
        defaultValues,
    });
    const {clearErrors, getValues, handleSubmit, setValue, watch} = methods;

    watch();

    const [errorMessage, setErrorMessage] = useState<string>();

    const {mutateAsync} = usePostDeletePreview();

    const onDeleteUser = useCallback(
        (value: boolean) => {
            const partnerIds = createPartnerIds(deletePartners);
            partnerIds.forEach((account) => {
                setValue(account, value);
            });
        },
        [deletePartners, setValue],
    );

    const deleteUser = getValues('delete_user');

    useEffect(() => {
        onDeleteUser(deleteUser);
    }, [deleteUser, onDeleteUser]);

    const onSubmit = async (formData: DeletionSubmissionFormData) => {
        const partnerObject = getPartnerObjectFromObject(formData);

        if (
            !hasMultiplePartners &&
            deletePartners.length > 0 &&
            !formData.delete_user &&
            !Object.values(partnerObject).includes(false)
        ) {
            return setErrorMessage(
                t`A user account must have at least one company. If you wish to delete all of your companies, you must also delete your user account.`,
            );
        }

        if (
            formData.delete_user &&
            Object.values(partnerObject).includes(false)
        ) {
            return setErrorMessage(
                t`You want to delete your account but you own these companies. Please either choose to delete those too, or go to their team member list page to transfer ownership to someone else and then come back to this deletion screen to continue.`,
            );
        }

        if (
            !formData.delete_user &&
            !Object.values(partnerObject).includes(true)
        ) {
            return setErrorMessage(
                t`You did not select anything for deletion.`,
            );
        }

        setErrorMessage(undefined);
        clearErrors();

        await mutateAsync(formData)
            .then((response) => {
                if (response) {
                    onSuccess({
                        formData,
                        previewData: response.data,
                    });
                }
            })
            .catch(onApiFormError(methods));
    };

    const informationSection = (
        <div>
            <strong>{t`Are you sure you don’t want to downgrade your account to a free account or manage your email preferences instead?`}</strong>
            <ul>
                <li>
                    <a href={window.Urls.billing_details()}>
                        {t`Billing Details`}
                    </a>
                </li>
                <li>
                    <a href={window.Urls.notifications_view()}>
                        {t`Notification Settings`}
                    </a>
                </li>
            </ul>
        </div>
    );
    const userAccountSection = (
        <div className="flex flex-col gap-2">
            <strong>{t`If you are sure that you want to continue, select the data that you wish to delete below.`}</strong>
            <Field label={t`User account`} type="checkbox">
                <Checkbox
                    aria-label="User Account"
                    label={userAccount.name}
                    name="delete_user"
                />
            </Field>
            {deletePartners.length > 0 && (
                <Information aria-label="user-warning" type="alert">
                    {t`Deleting your user account would also delete the following companies (Set someone else as the owner if you only want to delete your user account.)`}
                </Information>
            )}
        </div>
    );

    const companiesSection = deletePartners.length > 0 && (
        <Field className="mt-2" label={t`Companies`} type="checkbox">
            <fieldset disabled={deleteUser}>
                <CheckboxRadioGroup>
                    {deletePartners.map((account) => (
                        <Checkbox
                            aria-label={`partner-${account.mid}`}
                            key={account.mid}
                            label={
                                <>
                                    {account.name}{' '}
                                    <a
                                        href={`/co/${
                                            account.mid
                                        }${window.Urls.change_owner_react_view()}`}
                                    >
                                        {t`(Click here to change owner)`}
                                    </a>
                                </>
                            }
                            name={`delete_partner_${account.mid}`}
                        />
                    ))}
                </CheckboxRadioGroup>
            </fieldset>
        </Field>
    );

    const reasonsSection = hasReasons && (
        <>
            <Radio
                className="mt-2"
                label={t`Please select the main reason for deletion from the following.`}
                name="selected_reason"
                options={[
                    {
                        label: t`We don't create documents that much anymore`,
                        value: 'no_need_for_doc_management',
                    },
                    {
                        label: t`We decided to switch to a different product or system`,
                        value: 'switched_to_competitor',
                    },
                    {
                        label: t`MakeLeaps is missing important features`,
                        value: 'lack_of_feature',
                    },
                    {
                        label: t`We had a bad experience (bugs, designs, etc)`,
                        value: 'bad_user_experience',
                    },
                    {
                        label: t`It's too expensive for us`,
                        value: 'expensive',
                    },
                    {
                        label: t`Other`,
                        value: 'other',
                    },
                ]}
                required={true}
            />
            <TextArea
                aria-label="reason_detail"
                label={t`Please tell us more details or background of your deletion.`}
                name="reason_detail"
                required={true}
                rows={4}
            />
        </>
    );

    const improvementsSection = (
        <TextArea
            aria-label="improvements"
            label={t`Please let us know if there is any way we can improve our service.`}
            name="improvements"
            rows={4}
        />
    );

    const language = getPreferredLanguage();
    const handleContactUsClick = () => {
        if (language === 'ja') {
            window.location.href = 'https://www.makeleaps.jp/contact/';
        } else {
            window.location.href = 'https://www.makeleaps.com/en/plan-contact/';
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stacked className="max-w-xl">
                    {usingVba && (
                        <Information title={t`Warning`} type="alert">
                            <ul>
                                <li>
                                    {jt`You are currently using the Virtual Account feature. A written procedure is required to proceed. Please contact us for more details.`}
                                </li>
                            </ul>
                        </Information>
                    )}
                    {!usingVba && (
                        <>
                            {informationSection}
                            {userAccountSection}
                            {companiesSection}
                            {errorMessage && (
                                <Information
                                    aria-label="companies-warning"
                                    type="danger"
                                >
                                    {errorMessage}
                                </Information>
                            )}
                            {reasonsSection}
                            {improvementsSection}
                        </>
                    )}
                    <FormResult />
                    <FormActions>
                        <LinkButton href="/" kind="tertiary">
                            {t`Cancel`}
                        </LinkButton>
                        {usingVba ? (
                            <div className="grid rounded bg-green-200">
                                <SubmitButton onClick={handleContactUsClick}>
                                    {t`Contact Us`}
                                </SubmitButton>
                            </div>
                        ) : (
                            <SubmitButton kind="destructive" type="submit">
                                {t`Preview`}
                            </SubmitButton>
                        )}
                    </FormActions>
                </Stacked>
            </form>
        </FormProvider>
    );
};

export default HomeDeletionForm;
